import { apiClient, fetchMdmsMasters, tenantId } from "@/shared";
import { JURISDICTION_HIERARCHY } from "../utils/boundary";
import { createRequestInfo } from "@/shared/api/request-info";
import type {
  CreateOrganisationInput,
  CreateOrgUserInput,
  Organisation,
  OrganisationPage,
  OrganisationSearchParams,
  OrgApiContext,
  OrgJurisdiction,
  OrgRole,
  OrgRoleCatalog,
  OrgRoleGroup,
  OrgType,
  OrgUser,
  UpdateOrganisationInput,
  UpdateOrgUserInput,
} from "../types/organisation";

/**
 * vendor-registry client (`/vendor/organisation/v1/...`), written against the
 * contract the E4H Management Hub uses. Known backend gaps:
 *  - PoC password on org create and user password on user create/update are
 *    sent but ignored by the backend until those tasks ship.
 *  - Org search returns `orgPocPhone` encrypted until the backend fixes it.
 *  - Paging must go in the `Pagination` body key — the backend ignores the
 *    `limit`/`offset` query params the E4H UI sends.
 */
export const organisationApi = {
  searchOrganisations,
  getOrganisation,
  createOrganisation,
  updateOrganisation,
  searchOrgUsers,
  findOrganisationIdForUser,
  createOrgUser,
  updateOrgUser,
  deleteOrgUser,
  fetchRoleCatalog,
};

const ORG_BASE = "/vendor/organisation/v1";

interface OrganisationRecord {
  id?: string;
  code?: string;
  name?: string;
  orgType?: string;
  orgStatus?: string;
  orgPocName?: string;
  orgPocPhone?: string;
  orgPocEmail?: string;
  orgPocUsername?: string;
  defaultVendorName?: string;
  defaultVendorPhone?: string;
  defaultVendorEmail?: string;
  defaultVendorUsername?: string;
}

interface OrganisationResponse {
  organisations?: OrganisationRecord[];
  TotalCount?: number;
}

interface OrgUserRecord {
  id?: string;
  userId?: string;
  organizationId?: string;
  user?: {
    uuid?: string;
    name?: string;
    userName?: string;
    mobileNumber?: string;
    emailId?: string;
    roles?: Array<{ code?: string; name?: string }>;
    // Objects today; older records (and older Postman examples) hold plain boundary codes.
    jurisdiction?: Array<Partial<OrgJurisdiction> | string>;
  };
}

interface OrgUserResponse {
  OrgUsers?: OrgUserRecord[];
  TotalCount?: number;
}

/**
 * vendor-registry returns `orgPocPhone` encrypted on search (e.g. `159317|VRJL...`)
 * until the backend decrypts it. Treat that as "unknown" rather than showing
 * ciphertext — and never send it back on update, or it gets encrypted twice.
 */
function readablePhone(value: string | undefined): string | undefined {
  return value && !value.includes("|") ? value : undefined;
}

function toOrganisation(record: OrganisationRecord): Organisation {
  return {
    id: record.id ?? "",
    code: record.code,
    name: record.name ?? "",
    orgType: (record.orgType as OrgType) ?? "PLATFORM",
    status: record.orgStatus ?? "ACTIVE",
    pocName: record.orgPocName,
    pocPhone: readablePhone(record.orgPocPhone),
    pocEmail: record.orgPocEmail,
    pocUsername: record.orgPocUsername,
    defaultVendorName: record.defaultVendorName,
    defaultVendorPhone: readablePhone(record.defaultVendorPhone),
    defaultVendorEmail: record.defaultVendorEmail,
    defaultVendorUsername: record.defaultVendorUsername,
    raw: record as Record<string, unknown>,
  };
}

const TYPE_BY_DEPTH: OrgJurisdiction["boundaryType"][] = ["Country", "State", "District", "Block", "Facility"];

function toJurisdiction(item: Partial<OrgJurisdiction> | string): OrgJurisdiction {
  if (typeof item === "string") {
    // Plain code: infer the level from its depth (India_Karnataka_Udupi -> District).
    const depth = Math.min(item.split("_").length - 1, TYPE_BY_DEPTH.length - 1);
    return {
      hierarchy: JURISDICTION_HIERARCHY,
      boundary: item,
      boundaryType: TYPE_BY_DEPTH[depth],
      tenantId: tenantId(),
      isActive: true,
    };
  }
  return {
    id: item.id,
    hierarchy: item.hierarchy ?? JURISDICTION_HIERARCHY,
    boundary: item.boundary ?? "",
    boundaryType: item.boundaryType ?? "Country",
    tenantId: item.tenantId ?? tenantId(),
    isActive: item.isActive ?? true,
    ...(item.auditDetails ? { auditDetails: item.auditDetails } : {}),
  };
}

function toOrgUser(record: OrgUserRecord): OrgUser {
  return {
    orgUserId: record.id ?? "",
    userId: record.userId ?? record.user?.uuid ?? "",
    organisationId: record.organizationId ?? "",
    name: record.user?.name ?? "",
    userName: record.user?.userName ?? "",
    mobileNumber: record.user?.mobileNumber ?? "",
    emailId: record.user?.emailId,
    roleCodes: (record.user?.roles ?? []).map((role) => role.code ?? "").filter(Boolean),
    jurisdictions: (record.user?.jurisdiction ?? []).map(toJurisdiction),
    raw: (record.user ?? {}) as Record<string, unknown>,
  };
}

async function searchOrganisations(
  params: OrganisationSearchParams,
  ctx: OrgApiContext,
): Promise<OrganisationPage> {
  const { data } = await apiClient.post<OrganisationResponse>(`${ORG_BASE}/_search`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    SearchCriteria: {
      tenantId: tenantId(),
      orgType: params.orgType,
      ...(params.name?.trim() ? { name: params.name.trim() } : {}),
    },
    // The backend pages from this body key; the query params below mirror the
    // Postman collection / E4H calls and are harmless if ignored.
    Pagination: { limit: params.limit, offset: params.offset },
  }, { params: { tenantId: tenantId(), offset: params.offset, limit: params.limit } });

  const organisations = data.organisations?.map(toOrganisation) ?? [];
  return { organisations, total: data.TotalCount ?? organisations.length };
}

async function getOrganisation(id: string, ctx: OrgApiContext): Promise<Organisation | null> {
  const { data } = await apiClient.post<OrganisationResponse>(`${ORG_BASE}/_search`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    SearchCriteria: { tenantId: tenantId(), id },
  }, { params: { tenantId: tenantId(), offset: 0, limit: 1 } });
  const record = data.organisations?.[0];
  return record ? toOrganisation(record) : null;
}

async function createOrganisation(
  input: CreateOrganisationInput,
  ctx: OrgApiContext,
): Promise<Organisation> {
  const { data } = await apiClient.post<OrganisationResponse>(`${ORG_BASE}/_create`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    organisations: [
      {
        tenantId: tenantId(),
        name: input.name.trim(),
        orgType: input.orgType,
        orgStatus: input.status,
        orgPocName: input.pocName.trim(),
        orgPocPhone: input.pocPhone.trim(),
        ...(input.pocEmail?.trim() ? { orgPocEmail: input.pocEmail.trim() } : {}),
        orgPocUsername: input.pocUsername.trim(),
        orgPocPassword: input.pocPassword,
        ...(input.defaultVendorUsername
          ? {
              defaultVendorName: input.defaultVendorName?.trim(),
              defaultVendorPhone: input.defaultVendorPhone?.trim(),
              ...(input.defaultVendorEmail?.trim() ? { defaultVendorEmail: input.defaultVendorEmail.trim() } : {}),
              defaultVendorUsername: input.defaultVendorUsername.trim(),
              defaultVendorPassword: input.defaultVendorPassword,
            }
          : {}),
        isActive: true,
        orgAddress: [],
      },
    ],
  });
  return toOrganisation(data.organisations?.[0] ?? {});
}

async function updateOrganisation(
  organisation: Organisation,
  input: UpdateOrganisationInput,
  ctx: OrgApiContext,
): Promise<Organisation> {
  const { data } = await apiClient.post<OrganisationResponse>(`${ORG_BASE}/_update`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    organisations: [
      {
        ...organisation.raw,
        name: input.name.trim(),
        orgStatus: input.status,
        orgPocName: input.pocName.trim(),
        orgPocPhone: input.pocPhone.trim(),
        orgPocEmail: input.pocEmail?.trim() || undefined,
        isActive: true,
        // orgAddress is left as fetched (via `raw`) — overriding it would delete saved addresses.
      },
    ],
  });
  return toOrganisation(data.organisations?.[0] ?? {});
}

async function searchOrgUsersBy(
  criteria: { organizationIds?: string[]; userIds?: string[] },
  limit: number,
  ctx: OrgApiContext,
): Promise<OrgUserRecord[]> {
  const tenant = tenantId();
  const { data } = await apiClient.post<OrgUserResponse>(
    `${ORG_BASE}/user/_search`,
    {
      RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
      OrgUser: { tenantId: tenant, ...criteria },
    },
    // The URL tenantId must match OrgUser.tenantId.
    { params: { tenantId: tenant, limit, offset: 0, includeDeleted: false } },
  );
  return data.OrgUsers ?? [];
}

async function searchOrgUsers(organisationId: string, ctx: OrgApiContext): Promise<OrgUser[]> {
  const records = await searchOrgUsersBy({ organizationIds: [organisationId] }, 100, ctx);
  return records.map(toOrgUser);
}

async function findOrganisationIdForUser(userUuid: string, ctx: OrgApiContext): Promise<string | null> {
  const records = await searchOrgUsersBy({ userIds: [userUuid] }, 1, ctx);
  return records[0]?.organizationId ?? null;
}

function toRolePayload(roles: CreateOrgUserInput["roles"]) {
  const tenant = tenantId();
  return roles.map((role) => ({ code: role.code, name: role.name, tenantId: tenant }));
}

async function createOrgUser(input: CreateOrgUserInput, ctx: OrgApiContext): Promise<void> {
  await apiClient.post(`${ORG_BASE}/user/_create`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    organizationId: input.organisationId,
    user: {
      tenantId: tenantId(),
      name: input.name.trim(),
      userName: input.userName.trim(),
      password: input.password,
      mobileNumber: input.mobileNumber.trim(),
      ...(input.emailId?.trim() ? { emailId: input.emailId.trim() } : {}),
      roles: toRolePayload(input.roles),
      // vendor-registry's user key is singular `jurisdiction`.
      jurisdiction: input.jurisdictions,
    },
  });
}

async function updateOrgUser(input: UpdateOrgUserInput, ctx: OrgApiContext): Promise<void> {
  await apiClient.post(`${ORG_BASE}/user/_update`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    id: input.user.orgUserId,
    organizationId: input.user.organisationId,
    user: {
      ...input.user.raw,
      tenantId: tenantId(),
      name: input.name.trim(),
      mobileNumber: input.mobileNumber.trim(),
      emailId: input.emailId?.trim() || undefined,
      roles: toRolePayload(input.roles),
      // Existing jurisdictions (with their auditDetails) merged with new ones; removed ones go back with isActive: false.
      jurisdiction: input.jurisdictions,
      ...(input.newPassword ? { password: input.newPassword } : {}),
    },
  });
}

async function deleteOrgUser(user: OrgUser, ctx: OrgApiContext): Promise<void> {
  await apiClient.post(`${ORG_BASE}/user/_delete`, {
    RequestInfo: createRequestInfo(ctx.accessToken, ctx.user),
    id: user.orgUserId,
    userId: user.userId,
    organizationId: user.organisationId,
  });
}

async function fetchRoleCatalog(ctx: OrgApiContext): Promise<OrgRoleCatalog> {
  const masters = await fetchMdmsMasters(
    tenantId(),
    "Organisation",
    ["OrgRoles", "OrgRoleGroups"],
    ctx.accessToken,
    ctx.user,
  );
  return {
    roles: (masters.OrgRoles as OrgRole[] | undefined) ?? [],
    groups: (masters.OrgRoleGroups as OrgRoleGroup[] | undefined) ?? [],
  };
}
